import { SITE_ORIGIN } from './site';
import tiersData from './tiers.json';
import pageSeo from './pageSeo.json';

/**
 * Structured data (JSON-LD) shared by the React pages.
 *
 * scripts/prerender.mjs builds the same objects for the served HTML (it runs in
 * plain Node, so it cannot import this module). e2e/structured-data.spec.js checks,
 * page by page, that the JSON-LD in the served HTML and the JSON-LD after React has
 * mounted are identical, so the two copies cannot drift apart unnoticed.
 *
 * Rules (SEO phase 0): every value must match what is visible on the page; no
 * ratings, reviews or awards.
 *
 * Keep this module light: LandingPage imports it into the main bundle.
 */

/** Matches the Organization @id in public/index.html. */
export const ORG_ID = `${SITE_ORIGIN}/#organization`;
export const LOGO_URL = `${SITE_ORIGIN}/brand/mixtape-memories-icon.png`;
export const SHARE_IMAGE_URL = `${SITE_ORIGIN}/og-image.jpg`;

/** The publisher block for articles: the site Organization, with its PNG logo. */
export const publisherRef = {
  '@type': 'Organization',
  '@id': ORG_ID,
  name: 'Mixtape Memories',
  url: SITE_ORIGIN,
  logo: { '@type': 'ImageObject', url: LOGO_URL },
};

/** "450.00" from 45000 cents: schema.org prices are plain decimal strings. */
const priceString = (cents) => (cents / 100).toFixed(2);

/**
 * The homepage's three packages as one Product with three Offers. Every word comes
 * from tiers.json, the file the homepage pricing cards render: the offer name is the
 * card's title, the description is the card's effort line followed by its feature
 * list. Prices are AUD and include GST (valueAddedTaxIncluded), as the cards say.
 */
export const buildProductSchema = () => {
  const home = pageSeo.pages.home;
  return {
    '@context': 'https://schema.org',
    '@type': 'Product',
    '@id': `${SITE_ORIGIN}/#memorial-video`,
    name: 'Mixtape Memories memorial video',
    description: home.introBody,
    image: SHARE_IMAGE_URL,
    url: SITE_ORIGIN,
    // Typed in place, not just an @id pointer to the Organization block in
    // public/index.html: Google reads a bare { @id } here as an untyped object and
    // flags "Invalid object type for field 'brand'" (Google's structured-data check, 5 Oct 2026).
    brand: { '@type': 'Brand', name: 'Mixtape Memories' },
    offers: tiersData.tiers.map((tier) => ({
      '@type': 'Offer',
      name: tier.name,
      description: [tier.effort, ...tier.features].map(s => s.replace(/\.$/, '')).join('. ') + '.',
      price: priceString(tier.priceCents),
      priceCurrency: 'AUD',
      priceSpecification: {
        '@type': 'UnitPriceSpecification',
        price: priceString(tier.priceCents),
        priceCurrency: 'AUD',
        valueAddedTaxIncluded: true,
      },
      availability: 'https://schema.org/InStock',
      url: SITE_ORIGIN,
      seller: { '@type': 'Organization', '@id': ORG_ID, name: 'Mixtape Memories' },
      // Founder decision, 5 Oct 2026, for Google's shopping checks: the video is a
      // download, so delivery is free and digital, to Australia (the Organization's
      // areaServed). No delivery time is stated; none is promised on the site. Returns
      // are not permitted, as the Refund Policy says (no refunds once the video is
      // made, apart from technical failures and billing errors); the link carries
      // those exceptions, which this format has no field for.
      shippingDetails: {
        '@type': 'OfferShippingDetails',
        shippingRate: { '@type': 'MonetaryAmount', value: 0, currency: 'AUD' },
        shippingDestination: { '@type': 'DefinedRegion', addressCountry: 'AU' },
      },
      hasMerchantReturnPolicy: {
        '@type': 'MerchantReturnPolicy',
        applicableCountry: 'AU',
        returnPolicyCategory: 'https://schema.org/MerchantReturnNotPermitted',
        merchantReturnLink: `${SITE_ORIGIN}/refund-policy`,
      },
    })),
  };
};

/**
 * Home > Articles > [the guide it belongs to] > this article, matching the
 * breadcrumb shown at the top of every article page (ArticlePage.js). `parent` is
 * parentOf(article) from content/articles.js (passed in rather than imported, so
 * this module stays light enough for the landing page's main bundle).
 */
export const buildBreadcrumbSchema = (article, parent = null) => {
  const trail = [
    { name: 'Home', item: SITE_ORIGIN },
    { name: 'Articles', item: `${SITE_ORIGIN}/articles` },
    ...(parent ? [{ name: parent.title, item: `${SITE_ORIGIN}/${parent.slug}` }] : []),
    { name: article.title, item: `${SITE_ORIGIN}/${article.slug}` },
  ];
  return {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: trail.map((crumb, i) => ({ '@type': 'ListItem', position: i + 1, ...crumb })),
  };
};
